import { useState } from "react";

import type { InventoryItem } from "../api/types";
import { ErrorNotice } from "../components/ui";
import { ConfirmDialog } from "../products/ConfirmDialog";
import { thresholdLabel, variantLabel } from "./labels";
import { useSetThreshold } from "./useInventory";

/**
 * When this item should start warning — or whether it should at all.
 *
 * DELIBERATELY NOT PART OF THE ADJUST DIALOG. Changing the number at which a
 * warning appears moves no units, so it writes no stock movement; putting it
 * beside a quantity field would suggest it did, and an operator reading the
 * history afterwards would go looking for the units it moved.
 *
 * "Not configured" is a real state and is what every item starts in. There is
 * no default threshold: a warning nobody chose is a warning nobody acts on,
 * which is exactly what the old shop-wide constant of 5 produced. Zero is a
 * different answer again — warn me only when this has actually run out.
 */
export function LowStockThresholdDialog({
  item,
  onClose,
}: {
  item: InventoryItem;
  onClose: () => void;
}) {
  const [text, setText] = useState(
    item.low_stock_threshold === null ? "" : String(item.low_stock_threshold),
  );
  const save = useSetThreshold(item.inventory_item_id);

  // Null for an accessory that comes one way. The fact is then omitted rather
  // than written as "Variant: —", for the same reason as in the adjust dialog:
  // a dash in prose is a blank where a choice is implied.
  const variant = variantLabel(item);

  const trimmed = text.trim();
  const parsed = trimmed === "" ? null : Number.parseInt(trimmed, 10);
  const valid =
    parsed === null || (Number.isInteger(parsed) && (parsed as number) >= 0);

  return (
    <ConfirmDialog
      title="Set low-stock threshold"
      confirmLabel={parsed === null ? "Clear threshold" : "Save threshold"}
      busy={save.isPending}
      confirmDisabled={!valid || save.isPending}
      onCancel={onClose}
      onConfirm={() => {
        if (!valid) return;
        save.mutate(
          { expectedVersion: item.version, threshold: parsed },
          { onSuccess: onClose },
        );
      }}
    >
      <div className="stack" data-testid="threshold-dialog">
        <dl className="inventory__facts">
          <dt>Product</dt>
          <dd>{item.product_name}</dd>
          {variant !== null ? (
            <>
              <dt>Variant</dt>
              <dd>{variant}</dd>
            </>
          ) : null}
          <dt>Current</dt>
          <dd data-testid="threshold-current">
            {thresholdLabel(item.low_stock_threshold)}
          </dd>
        </dl>

        <div className="inventory__field">
          <label className="inventory__label" htmlFor="threshold-value">
            Warn at or below
          </label>
          <input
            id="threshold-value"
            className="num"
            type="number"
            min={0}
            step={1}
            style={{ width: 140 }}
            value={text}
            autoFocus
            placeholder="Not configured"
            onChange={(event) => setText(event.target.value)}
          />
          <p className="inventory__hint">
            Leave empty to switch low-stock warnings off for this item. Zero
            means warn only once it has run out.
          </p>
          {!valid ? (
            <p className="field-error">
              A threshold is a whole number of zero or more.
            </p>
          ) : null}
        </div>

        <p className="muted">
          This changes when the warning appears. It moves no stock and records
          no movement.
        </p>

        {save.error ? <ErrorNotice error={save.error} /> : null}
      </div>
    </ConfirmDialog>
  );
}
